/**
 * Person-level lookup on the Monday **Leads** board, shared by every inbound
 * source that merges a new artefact onto the person's open card (ÖPM's four
 * lead kinds, LandVest's e-mail intake).
 */

import {
  findItemsByColumnValue,
  type MondayClient,
  type MondayConfig,
  type MondayItemMatch,
} from "./monday-client";
import { normalizePhone } from "./phone";

/**
 * Statuses that mean "sales is done with this card".
 *
 * A new artefact from the same person must NOT reopen a lost deal or a completed
 * qualification — it gets a fresh card, and the closed one stays as history.
 * These strings are the live labels on `lead_status` (read back 2026-08-28:
 * Neuer Lead · Outbound · Verloren · Unvollständig · Vollständig).
 */
const CLOSED_LEAD_STATUSES = new Set(["Vollständig", "Verloren"]);

/** One of a person's open lead cards, and what it is. */
export interface OpenLeadCard {
  id: string;
  /** Its `Lead-Typ` label as the board shows it. */
  leadType: string | null;
}

/**
 * The pulse id of an OPEN lead already on the board for this person, or null:
 * the first of `findOpenLeadsForPerson`.
 */
export async function findOpenLeadForPerson(
  client: MondayClient,
  cfg: MondayConfig,
  keys: string[]
): Promise<string | null> {
  const [open] = await findOpenLeadsForPerson(client, cfg, keys);
  return open?.id ?? null;
}

/**
 * The OPEN leads already on the board for this person, in the order Monday
 * returns them, each with its `Lead-Typ`, so the caller can decide which one a
 * new artefact may join.
 *
 * Takes every key form the person could be filed under, because the ladder in
 * `./identity` deliberately does not collapse them: an anonymous funnel
 * session files `mail:…` and the same human's registered account files
 * `user:…`.
 *
 * Filters before reusing an existing card:
 *   - BOARD. A match on another board is not ours to touch — six of our leads
 *     already sit on the Deals board, and Monday rejects the ENTIRE mutation
 *     on one unknown column id, so writing lead columns there loses
 *     everything.
 *   - STATUS. A closed card is history; a new artefact must not reopen it.
 *   - TYPE. A LandVest analysis remains a separate lead, even for the same
 *     person; other artefacts must not be appended to that analysis card.
 *   - PAGINATION. Analyses and closed cards can fill a page. Exhaust the
 *     person-key matches before treating a missing eligible card as permission
 *     to create a new lead.
 */
export async function findOpenLeadsForPerson(
  client: MondayClient,
  cfg: MondayConfig,
  keys: string[]
): Promise<OpenLeadCard[]> {
  if (!cfg.leadsBoardId || !cfg.leadColumns.personKey) return [];

  // Search every key form together: multiple values are an OR. Follow the
  // matching pages so separate LandVest analyses cannot hide an open card.
  const matches = await findItemsByColumnValue(
    client,
    cfg.leadsBoardId,
    cfg.leadColumns.personKey,
    keys,
    {
      limit: 20,
      allPages: true,
      withBoard: true,
      readColumns: [cfg.leadColumns.status, cfg.leadColumns.leadType].filter(
        (column): column is string => Boolean(column)
      ),
    }
  );

  // FAIL CLOSED on an unreadable status. `MONDAY_LEAD_COL_STATUS=""` is the
  // documented way to disable a column, and it also empties `readColumns`, so
  // a permissive reading would treat every card as open — reopening a lead
  // sales marked „Verloren" and overwriting it, reached through a supported
  // config. If we cannot tell whether a card is closed, we do not merge onto it.
  return matches
    .filter((m) => isOpenPersonCard(m, cfg))
    .map((m) => ({ id: m.id, leadType: m.columns[cfg.leadColumns.leadType!] ?? null }));
}

/**
 * One rule for "this match is the person's open card", shared by the merge above and the
 * identity fill below. Needs the status and lead-type columns in the match's `columns`.
 */
function isOpenPersonCard(m: MondayItemMatch, cfg: MondayConfig): boolean {
  const statusColumn = cfg.leadColumns.status;
  const typeColumn = cfg.leadColumns.leadType;
  if (!statusColumn || !typeColumn) return false;
  const status = m.columns[statusColumn];
  const leadType = m.columns[typeColumn];
  return (
    m.boardId === cfg.leadsBoardId &&
    !!status &&
    !CLOSED_LEAD_STATUSES.has(status) &&
    leadType !== undefined &&
    leadType !== "LandVest-Analyse"
  );
}

/** What a person told us about themselves, already normalised by the caller. */
export interface LeadIdentity {
  /** A real name; null when the person has given none (never a fallback). */
  name: string | null;
  company: string | null;
  phone: { e164: string; country: string } | null;
}

/** Identity values as written onto one card (null field = none written). */
export interface LeadIdentityValues {
  name: string | null;
  company: string | null;
  phoneE164: string | null;
}

/**
 * What we last wrote onto one card, and what we were about to write (`pending`). Saved
 * BEFORE the Monday write and settled after it, so whichever of the two the card shows
 * after an interruption is recognised as the customer's own.
 */
export interface LeadIdentityBaseline extends LeadIdentityValues {
  pending: LeadIdentityValues | null;
}

/**
 * The names the lead projections give a card when the person gave none (each projection's
 * `nameIsPlaceholder` case). One list, so the identity fill recognises every one of them.
 */
export const LEAD_PLACEHOLDER_NAMES = {
  request: "Unbekannter Interessent",
  offering: "Unbekannter Anbieter",
  callback: "Rückruf ohne Namen",
  contact: "Kontaktformular ohne Namen",
} as const;
const PLACEHOLDER_NAMES = new Set<string>(Object.values(LEAD_PLACEHOLDER_NAMES));

/**
 * A card name the projections fall back to when the person gave no name: one of
 * `LEAD_PLACEHOLDER_NAMES`, their e-mail address or a raw phone number.
 */
export function isPlaceholderLeadName(name: string | null | undefined): boolean {
  const n = name?.trim() ?? "";
  // A raw number in any format the projections accept (`normalizePhone`), not a separator
  // list — but only a title that IS a number: `normalizePhone` drops letters, so a sales
  // title like „Frau Hartmann (0231 …)" must not count.
  const isNumber = !/\p{L}/u.test(n) && (normalizePhone(n) !== null || /^[+\d\s()/.-]+$/.test(n));
  return !n || PLACEHOLDER_NAMES.has(n) || /^[^\s@]+@[^\s@]+$/.test(n) || isNumber;
}

/** A phone cell's text („+49 231 0000111") and an E.164 compare by their digits. */
const digits = (phone: string | null | undefined) => (phone ?? "").replace(/\D/g, "");

/**
 * This person's OPEN cards on the Leads board — the same filter as the merge above — with
 * their company and phone read back. The caller passes only keys that prove the person.
 */
export async function findOpenPersonCards(
  client: MondayClient,
  cfg: MondayConfig,
  keys: string[]
): Promise<MondayItemMatch[]> {
  const c = cfg.leadColumns;
  if (!cfg.leadsBoardId || !c.personKey || !c.status || !c.leadType) return [];
  const matches = await findItemsByColumnValue(client, cfg.leadsBoardId, c.personKey, keys, {
    limit: 20,
    allPages: true,
    withBoard: true,
    readColumns: [c.status, c.leadType, c.company, c.phone].filter((column): column is string =>
      Boolean(column)
    ),
  });
  return matches.filter((m) => isOpenPersonCard(m, cfg));
}

/**
 * The identity columns to write onto one card. Pure.
 *
 * `observed` is what the card shows of our record now: where it shows the pending value of
 * an interrupted write, that value is promoted, so a new pending write never loses it.
 *
 * Per field, one rule, and only towards a value the customer has: a gap (placeholder name,
 * empty company or phone) is filled; a value that equals our record is the customer's own
 * and follows them („Lena" becomes „Lena Hartmann"); anything else is a sales edit or a
 * source-specific answer (a callback's number) and stays. Nothing is ever cleared: a field
 * missing from the profile is not a deletion. `values` is the record once `columns` are
 * written; it moves only where we write.
 */
export function planLeadIdentity(
  card: MondayItemMatch,
  cfg: MondayConfig,
  identity: LeadIdentity,
  baseline: LeadIdentityBaseline | null
): { columns: Record<string, unknown>; observed: LeadIdentityValues; values: LeadIdentityValues } {
  const c = cfg.leadColumns;
  const columns: Record<string, unknown> = {};
  const shown = {
    name: card.name.trim(),
    company: c.company ? (card.columns[c.company]?.trim() ?? "") : "",
    phoneE164: c.phone ? digits(card.columns[c.phone]) : "",
  };
  const comparable = (field: keyof LeadIdentityValues, v: string | null | undefined) =>
    field === "phoneE164" ? digits(v) : (v ?? "");
  const observed = { ...NO_VALUES };
  for (const field of FIELDS) {
    const pending = baseline?.pending?.[field];
    observed[field] =
      pending && comparable(field, pending) === shown[field]
        ? pending
        : (baseline?.[field] ?? null);
  }
  const values = { ...observed };
  const targets: LeadIdentityValues = {
    name: identity.name,
    company: identity.company,
    phoneE164: identity.phone?.e164 ?? null,
  };
  for (const field of FIELDS) {
    if (field !== "name" && !(field === "company" ? c.company : c.phone)) continue;
    const target = targets[field];
    if (!target) continue;
    const now = shown[field];
    const gap = field === "name" ? isPlaceholderLeadName(now) : !now;
    const owned = !!now && !!observed[field] && comparable(field, observed[field]) === now;
    if (now !== comparable(field, target) && (gap || owned)) {
      if (field === "name") columns.name = target;
      else if (field === "company") columns[c.company!] = target;
      else
        columns[c.phone!] = {
          phone: identity.phone!.e164,
          countryShortName: identity.phone!.country,
        };
      values[field] = target;
    }
    // A matching value we did not write is NOT adopted: equality is not provenance, and a
    // callback's own number must not become the profile's by coincidence.
  }
  return { columns, observed, values };
}

const FIELDS = ["name", "company", "phoneE164"] as const;
const NO_VALUES: LeadIdentityValues = { name: null, company: null, phoneE164: null };

/** Write identity columns onto one lead card; throws when Monday does not apply them. */
export async function writeLeadColumns(
  client: MondayClient,
  cfg: MondayConfig,
  itemId: string,
  columns: Record<string, unknown>
): Promise<void> {
  const updated = (await client.gql(
    `mutation($b:ID!,$i:ID!,$c:JSON!){
      change_multiple_column_values(board_id:$b,item_id:$i,column_values:$c){ id }
    }`,
    { b: cfg.leadsBoardId, i: itemId, c: JSON.stringify(columns) }
  )) as { change_multiple_column_values?: { id?: string } } | null;
  if (!updated?.change_multiple_column_values?.id) {
    throw new Error(`Monday did not apply the identity update to lead item ${itemId}`);
  }
}
